/**
 * Reine Parser-Funktionen für HBF/FBF-Strings aus dem GeoPackage-Punkt-Import.
 *
 * Wird von [import-wizard.component.ts](import-wizard.component.ts) genutzt. Keine
 * Angular-Abhaengigkeit, damit das Modul ohne TestBed unit-testbar ist.
 *
 * Bug-Hintergrund (siehe pkt.gpkg-Stichprobe von 2026-05):
 *  - HBFs kommen oft ohne Leerzeichen zwischen Substratpraefix und Code (z. B. `"sUGUP"`
 *    statt `"s UGUP"`). normalizeHbf() heilt das.
 *  - BFs nutzen `-` UND `_` als Suffix-Trenner mit 1-4 Buchstaben (`-msf`, `_mhf`).
 *    Der alte Parser akzeptierte nur `-` mit max. 2 Buchstaben.
 *  - BFs koennen ueber `//` zwei FBFs ("begrabener Boden") tragen — wurde vorher
 *    als ein zusammengeklebter String genommen.
 */

/** FBF-Praefixe — fixe Liste laut Hilfe „FBFPrefix" (data-model.ts:493). */
export const FBF_PREFIXES = ['Ap', 'po', 'rb', 'tp', 'b', 'g', 'h', 'o', 'p', 'q', 's', 'w', 'a', 'x'] as const;

/** Bekannte Bodensubstrat-Suffixe nach `/` (laut import.service.ts:26). */
export const KNOWN_BODENSUBSTRAT_SUFFIXES = [
	'KO', 'KS', 'KL', 'lS', 'ko', 'kl', 'ls',
	'L', 'E', 'K', 'C', 'O', 'T', 'S',
	'l', 'e', 'k', 'c', 'o', 't',
] as const;

/** Stauwasser-Substrat-Praefixe (laengere zuerst). */
const STAUWASSER_PRAEFIXE = ['dL', 'tL', 'dT', 'tT', 'dK', 'tK', 'L', 'U', 'T', 'K'] as const;

export interface ParsedBfPair {
	trenner: '' | '/' | '//';
	prefix: string;
	fbf: string;
}

export interface ParsedFeinbodenform {
	bfPairs: ParsedBfPair[];
	/** Bodensubstrat-Suffix mit fuehrendem `/`, z. B. `"/L"`. Bleibt leer, wenn keiner. */
	suffix: string;
	/** Substratfeuchte-/Mesoklima-Code, OHNE Trenner (z. B. `"msf"`). Auf Bindestrich-Form normiert,
	 *  d. h. `"-msf"` und `"_msf"` ergeben beide `"msf"`. Leer, wenn keiner. */
	substratFeuchte: string;
	/** Reliktischer Go-Horizont — `(G)` im Eingabestring. */
	reliktG: boolean;
	/** Stauwasserstufe — Zahl vor `/` bei Format Zahl/Zahl, sonst gesetzt wenn Substrat als Stauwasser klassifiziert. */
	stauwasser: string;
	/** Grundwasserstufe — Zahl nach `/` bei Format Zahl/Zahl, sonst gesetzt wenn Substrat als Grundwasser klassifiziert. */
	grundwasser: string;
}

/**
 * Normalisiert eine HBF-Zeichenkette: stellt sicher, dass zwischen dem Substratpraefix
 * (fuehrende Kleinbuchstaben) und dem Code (ab erstem Grossbuchstaben) genau ein
 * Leerzeichen steht.
 *
 * Beispiele:
 *   "sUGUP"   → "s UGUP"
 *   "yosK"    → "yos K"
 *   "kolUG"   → "kol UG"
 *   "s GSP"   → "s GSP" (unveraendert)
 *   "GSP"     → "GSP"   (kein Praefix)
 *   ""        → ""
 *
 * HBFs mit `/` (compound HBFs) werden NICHT zerlegt — Aufrufer entscheidet, ob
 * `parseHbfList()` zum Zuge kommt.
 */
export function normalizeHbf(input: string): string {
	if (!input) return '';
	const trimmed = input.trim();
	if (!trimmed) return '';
	if (trimmed.includes('/')) return trimmed; // compound HBFs hier nicht anpacken

	const m = trimmed.match(/^([a-zäöü]+)\s*(.*)$/);
	if (!m) return trimmed;          // beginnt nicht mit Kleinbuchstaben
	const prefix = m[1];
	const rest = m[2].trim();
	if (!rest) return prefix;        // nur Praefix, kein Code
	return `${prefix} ${rest}`;
}

/**
 * Spaltet einen HBF-String mit `/` und `//` in einzelne HBFs auf und normalisiert jede.
 * Im Gegensatz zu [import.service.ts:62](../import.service.ts) parseHbfString) wird hier KEIN Katalog-Lookup
 * gemacht — wir trennen nur an jedem Slash und reichen das Ergebnis durch normalizeHbf().
 *
 * Beispiele:
 *   "sSP/sGSP" → [{ trenner: '',  hbf: 's SP'  }, { trenner: '/',  hbf: 's GSP' }]
 *   "rl/sF"    → [{ trenner: '',  hbf: 'rl'    }, { trenner: '/',  hbf: 's F'   }]
 *   "X//Y"     → [{ trenner: '',  hbf: 'X'     }, { trenner: '//', hbf: 'Y'     }]
 */
export function parseHbfList(input: string): { trenner: '' | '/' | '//'; hbf: string }[] {
	if (!input) return [];
	const trimmed = input.trim();
	if (!trimmed) return [];

	const out: { trenner: '' | '/' | '//'; hbf: string }[] = [];
	const parts = trimmed.split(/(\/\/|\/)/);
	let trenner: '' | '/' | '//' = '';
	for (const part of parts) {
		if (part === '//' || part === '/') {
			trenner = part as '/' | '//';
		} else if (part.trim()) {
			out.push({ trenner, hbf: normalizeHbf(part) });
			trenner = '';
		}
	}
	return out;
}

/**
 * Parst eine kombinierte Feinbodenform-Zeichenkette aus dem GeoPackage-Import.
 *
 * Format-Bestandteile (in Strip-Reihenfolge):
 *  1. `(G)` reliktischer Go-Horizont
 *  2. Substratfeuchte/Mesoklima-Suffix `[-_]xxxx` (1-4 Kleinbuchstaben am Ende)
 *  3. Bodensubstrat-Suffix `/X` (X aus KNOWN_BODENSUBSTRAT_SUFFIXES, am Ende)
 *  4. Wasserstufen — Zahlen am Ende: `\d+` oder `\d+/\d+` (Stau/Grund)
 *  5. Aufspalten an `//` (begrabener Boden) und `/` (compound FBFs)
 *  6. Pro Glied: FBF-Praefix vom Anfang strippen
 *
 * Beispiele:
 *   "StSB-msf"          → bfPairs:[{prefix:'',  fbf:'StSB'}],     substratFeuchte:'msf'
 *   "ApLbSE66-msf"      → bfPairs:[{prefix:'Ap',fbf:'LbSE'}],     grundwasser:'66', substratFeuchte:'msf'
 *   "FlSE37/55_msf"     → bfPairs:[{prefix:'',  fbf:'FlSE'}],     stauwasser:'37', grundwasser:'55', substratFeuchte:'msf'
 *   "StSU//KfLG55"      → bfPairs:[{prefix:'',fbf:'StSU'}, {trenner:'//',prefix:'',fbf:'KfLG'}], stauwasser:'55'
 *   "ApChS/L"           → bfPairs:[{prefix:'Ap',fbf:'ChS'}],      suffix:'/L'
 *   "ApNßS/StSB67_msf"  → bfPairs:[{prefix:'Ap',fbf:'NßS'}, {trenner:'/',prefix:'',fbf:'StSB'}], grundwasser:'67', substratFeuchte:'msf'
 */
export function parseFeinbodenform(input: string): ParsedFeinbodenform {
	const empty: ParsedFeinbodenform = {
		bfPairs: [], suffix: '', substratFeuchte: '', reliktG: false, stauwasser: '', grundwasser: '',
	};
	if (!input) return empty;
	let remaining = input.trim();
	if (!remaining) return empty;

	// 1. (G)
	let reliktG = false;
	if (/\(G\)/.test(remaining)) {
		reliktG = true;
		remaining = remaining.replace(/\s*\(G\)\s*/, '').trim();
	}

	// 2. Substratfeuchte/Mesoklima — `[-_]([a-zäöü]{1,4})` am Ende
	let substratFeuchte = '';
	const sfMatch = remaining.match(/[-_]([a-zäöü]{1,4})$/i);
	if (sfMatch) {
		substratFeuchte = sfMatch[1];
		remaining = remaining.substring(0, remaining.length - sfMatch[0].length);
	}

	// 3. Bodensubstrat-Suffix — `/X` am Ende, X aus KNOWN_BODENSUBSTRAT_SUFFIXES
	//    Wichtig: KEIN Teil von `//`. Die KNOWN-Liste ist nach Laenge-desc sortiert,
	//    damit z. B. `KL` vor `L` greift.
	let suffix = '';
	for (const s of KNOWN_BODENSUBSTRAT_SUFFIXES) {
		const candidate = '/' + s;
		if (remaining.endsWith(candidate)) {
			// Sicherstellen, dass der Slash kein Teil von `//` ist
			const slashPos = remaining.length - candidate.length;
			if (slashPos === 0 || remaining[slashPos - 1] !== '/') {
				suffix = candidate;
				remaining = remaining.substring(0, slashPos);
				break;
			}
		}
	}

	// 4. Wasserstufen — `\d+(?:\/\d+)?` am Ende
	let stauwasser = '';
	let grundwasser = '';
	const wMatch = remaining.match(/(\d+)(?:\/(\d+))?$/);
	if (wMatch) {
		const num1 = wMatch[1];
		const num2 = wMatch[2] || '';
		const stripped = remaining.substring(0, remaining.length - wMatch[0].length);
		if (num2) {
			// Format Zahl/Zahl → Stau/Grund
			stauwasser = num1;
			grundwasser = num2;
		} else {
			// Einzelzahl: anhand letzter FBF klassifizieren. Letzte FBF = nach letztem `/` oder `//`.
			const lastSegment = stripped.split(/\/\/|\//).pop() || '';
			const fbfOnly = stripFbfPrefix(lastSegment).fbf;
			if (isStauwasserSubstrat(fbfOnly)) stauwasser = num1;
			else grundwasser = num1;
		}
		remaining = stripped;
	}

	// 5. Aufspalten an // und / und pro Glied Praefix extrahieren
	const bfPairs: ParsedBfPair[] = [];
	const tokens = remaining.split(/(\/\/|\/)/);
	let trenner: '' | '/' | '//' = '';
	for (const tok of tokens) {
		if (tok === '//' || tok === '/') {
			trenner = tok as '/' | '//';
			continue;
		}
		const t = tok.trim();
		if (!t) continue;
		const { prefix, fbf } = stripFbfPrefix(t);
		bfPairs.push({ trenner, prefix, fbf });
		trenner = '';
	}

	return { bfPairs, suffix, substratFeuchte, reliktG, stauwasser, grundwasser };
}

/**
 * Greift einen oder mehrere FBF-Praefixe (FBF_PREFIXES) vom Anfang ab und gibt
 * Praefix + Rest zurueck. Wenn keiner matcht, ist prefix leer und fbf das gesamte Token.
 *
 * Sonderregel "ap": Bevorzugt "Ap" (Capital) vor "a"+"p" — sonst rutscht "ap" als a+p durch.
 */
export function stripFbfPrefix(input: string): { prefix: string; fbf: string } {
	if (!input) return { prefix: '', fbf: '' };
	let remaining = input;
	let prefix = '';
	let foundOne = true;
	while (foundOne && remaining.length > 0) {
		foundOne = false;
		for (const p of FBF_PREFIXES) {
			if (!remaining.startsWith(p)) continue;
			// "a" darf nicht vor "p" matchen — sonst rutscht "ap" als a+p durch.
			if (p === 'a' && remaining.length > 1 && remaining[1] === 'p') continue;
			const after = remaining.substring(p.length);
			// Praefix nur greifen, wenn dahinter ein Capital, ein weiterer Praefix oder Ende kommt.
			if (after.length === 0 || /^[A-ZÄÖÜ]/.test(after) || FBF_PREFIXES.some(px => after.startsWith(px))) {
				prefix += p;
				remaining = after;
				foundOne = true;
				break;
			}
		}
	}
	return { prefix, fbf: remaining };
}

/**
 * Prueft, ob ein FBF-Kuerzel zu Stauwasser gehoert. FBF-Aufbau:
 *   `[Individualteil = Capital+lowercase][Substrat-Praefix...]`
 * Stauwasser-Substrate beginnen mit L/U/T/K bzw. dL/tL/dT/tT/dK/tK; alles andere = Grundwasser.
 *
 * Erwartet bereits praefix-bereinigte Eingabe — `stripFbfPrefix()` zuerst.
 */
export function isStauwasserSubstrat(fbf: string): boolean {
	if (!fbf || fbf.length <= 2) return false;
	const m = fbf.match(/^[A-ZÄÖÜ][a-zäöü]/);
	if (!m) return false;
	const kuerzel = fbf.substring(m[0].length);
	if (!kuerzel) return false;
	for (const p of STAUWASSER_PRAEFIXE) {
		if (kuerzel.startsWith(p)) return true;
	}
	return false;
}
